import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, MapPin, Briefcase, GraduationCap, Mail, Phone } from 'lucide-react';
import { mockMatches } from '@/data/mockMatches';
import { useAuth } from '@/contexts/AuthContext';

// Mock candidate profile - will be replaced with Convex
const mockCandidateProfile = {
  name: 'Ola Nordmann',
  title: 'Senior Frontend Utvikler',
  location: 'Oslo, Norge',
  email: 'ola.nordmann@example.com',
  phone: '+47 123 45 678',
  bio: 'Erfaren frontend-utvikler med lidenskap for brukeropplevelse og moderne webteknologi. Spesialisert i React og TypeScript.',
  experience: [
    {
      title: 'Senior Frontend Utvikler',
      company: 'Tech Solutions AS',
      period: '2020 - Nå',
      description: 'Leder utvikling av moderne webapplikasjoner med React og TypeScript.'
    },
    {
      title: 'Frontend Utvikler',
      company: 'Digital Agency',
      period: '2018 - 2020',
      description: 'Utviklet responsive nettsider og webapplikasjoner for ulike kunder.'
    }
  ],
  education: [
    {
      degree: 'Master i Informatikk',
      school: 'Universitetet i Oslo',
      period: '2015 - 2018'
    }
  ],
  skills: ['React', 'TypeScript', 'Node.js', 'Next.js', 'Tailwind CSS', 'Git']
};

export default function ChatPage() {
  const { user } = useAuth();
  const isEmployer = user?.role === 'employer';
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<typeof mockCandidateProfile | null>(null);

  // Mock candidate data - will be replaced with actual data from Convex
  const candidateData = [
    { name: 'Ola Nordmann', image: '/ola.jpg' },
    { name: 'Kari Hansen', image: '/kari.jpg' },
    { name: 'Per Olsen', image: '/ola.jpg' },
    { name: 'Sara Berg', image: '/kari2.jpg' },
    { name: 'Erik Johansen', image: '/profilbilde.png' },
    { name: 'Nina Kristiansen', image: '/kari.jpg' },
    { name: 'Thomas Andreassen', image: '/ola.jpg' },
    { name: 'Maria Pettersen', image: '/kari2.jpg' }
  ];

  // Transform matches to conversations - will be replaced with Convex queries
  const mockConversations = mockMatches.map((match, index) => {
    const candidate = candidateData[index] || { name: `Kandidat #${index + 1}`, image: null };
    const displayName = isEmployer 
      ? (match.isRevealed ? candidate.name : `Kandidat #${index + 1}`)
      : match.company;
    
    return {
      id: match.id,
      displayName,
      displaySubtitle: isEmployer ? match.jobTitle : match.lastMessage || 'Start en samtale!',
      company: match.company,
      jobTitle: match.jobTitle,
      lastMessage: match.lastMessage || 'Start en samtale!',
      timestamp: match.lastMessageAt || match.matchedAt,
      unreadCount: match.unreadCount,
      isRevealed: match.isRevealed,
      profileImage: match.isRevealed ? candidate.image : null,
      logo: '/placeholder-logo.png',
    };
  });

  const handleProfileClick = (e: React.MouseEvent, isRevealed: boolean) => {
    if (isRevealed) {
      e.preventDefault();
      e.stopPropagation();
      setSelectedProfile(mockCandidateProfile);
      setShowProfileModal(true);
    }
  };

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="p-4">
        
        {mockConversations.length > 0 ? (
          <div className="space-y-3">
            {mockConversations.map((conv) => (
              <Link
                key={conv.id}
                to={`/chat/${conv.id}`}
                className="block p-4 rounded-lg transition-all duration-[var(--motion-duration-medium)] hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                  boxShadow: 'var(--shadow-card)'
                }}
              >
                <div className="flex items-center gap-3">
                  <div 
                    onClick={(e) => handleProfileClick(e, conv.isRevealed)}
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-transform overflow-hidden"
                    style={{ 
                      backgroundColor: conv.isRevealed ? '#10b981' : 'var(--color-bg-accent)',
                      cursor: conv.isRevealed ? 'pointer' : 'default',
                      transform: conv.isRevealed ? 'scale(1)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (conv.isRevealed) {
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (conv.isRevealed) {
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {conv.isRevealed && conv.profileImage ? (
                      <img 
                        src={conv.profileImage} 
                        alt={conv.displayName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to initial if image fails
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = `
                            <span style="color: white; font-weight: var(--font-weight-bold);">
                              ${conv.displayName.charAt(0)}
                            </span>
                          `;
                        }}
                      />
                    ) : (
                      <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-weight-bold)' }}>
                        {conv.displayName.charAt(0)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)', fontSize: 'var(--font-size-body-lg)' }}>
                      {conv.displayName}
                    </h3>
                    <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-body-md)' }}>
                      {conv.displaySubtitle}
                    </p>
                  </div>
                  {conv.unreadCount > 0 && (
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold"
                      style={{ 
                        backgroundColor: 'var(--color-bg-accent)',
                        color: 'var(--color-text-primary)'
                      }}
                    >
                      {conv.unreadCount}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p style={{ color: 'var(--color-text-secondary)' }}>Ingen meldinger ennå.</p>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-caption)' }} className="mt-2">
              Match med bedrifter for å starte en samtale!
            </p>
          </div>
        )}
      </div>

      {/* Candidate Profile Modal */}
      {showProfileModal && selectedProfile && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          onClick={() => setShowProfileModal(false)}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          />

          {/* Modal */}
          <div
            className="relative w-full h-[90vh] overflow-y-auto rounded-t-3xl shadow-xl"
            style={{ backgroundColor: 'var(--color-bg-primary)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Profile Image */}
            <div
              className="sticky top-0 z-10 border-b"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
              }}
            >
              <div className="relative">
                {/* Background gradient */}
                <div 
                  className="h-32"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  }}
                />
                
                {/* Profile Image */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
                  <div className="w-24 h-24 rounded-full border-4 overflow-hidden"
                    style={{ 
                      borderColor: 'var(--color-bg-primary)',
                      backgroundColor: '#10b981'
                    }}
                  >
                    <img 
                      src="/ola.jpg" 
                      alt={selectedProfile.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to initials if image doesn't load
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = `
                          <div class="w-full h-full flex items-center justify-center" style="background-color: #10b981">
                            <span style="color: white; font-size: 2rem; font-weight: bold;">
                              ${selectedProfile.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                        `;
                      }}
                    />
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>

              {/* Name and Title */}
              <div className="text-center pt-14 pb-6 px-6">
                <h2
                  className="text-2xl font-bold mb-1"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {selectedProfile.name}
                </h2>
                <p
                  className="text-lg mb-3"
                  style={{ color: '#10b981' }}
                >
                  {selectedProfile.title}
                </p>
                <div className="flex items-center justify-center gap-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{selectedProfile.location}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div 
              className="p-6 space-y-6"
              style={{ backgroundColor: 'var(--color-bg-primary)' }}
            >
              {/* Contact Info */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Kontaktinformasjon
                </h3>
                <div className="space-y-2">
                  <a 
                    href={`mailto:${selectedProfile.email}`}
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-white/5 transition-colors"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    <Mail className="w-5 h-5" style={{ color: '#10b981' }} />
                    <span>{selectedProfile.email}</span>
                  </a>
                  <a 
                    href={`tel:${selectedProfile.phone}`}
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-white/5 transition-colors"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    <Phone className="w-5 h-5" style={{ color: '#10b981' }} />
                    <span>{selectedProfile.phone}</span>
                  </a>
                </div>
              </div>

              {/* Bio */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Om meg
                </h3>
                <p
                  className="leading-relaxed"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {selectedProfile.bio}
                </p>
              </div>

              {/* Experience */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3 flex items-center gap-2"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <Briefcase className="w-5 h-5" style={{ color: '#10b981' }} />
                  Erfaring
                </h3>
                <div className="space-y-4">
                  {selectedProfile.experience.map((exp, index) => (
                    <div key={index} className="pl-4 border-l-2" style={{ borderColor: '#10b981' }}>
                      <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        {exp.title}
                      </h4>
                      <p className="text-sm" style={{ color: '#10b981' }}>
                        {exp.company}
                      </p>
                      <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>
                        {exp.period}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {exp.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Education */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3 flex items-center gap-2"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <GraduationCap className="w-5 h-5" style={{ color: '#10b981' }} />
                  Utdanning
                </h3>
                <div className="space-y-3">
                  {selectedProfile.education.map((edu, index) => (
                    <div key={index} className="pl-4 border-l-2" style={{ borderColor: '#10b981' }}>
                      <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        {edu.degree}
                      </h4>
                      <p className="text-sm" style={{ color: '#10b981' }}>
                        {edu.school}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        {edu.period}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Skills */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Ferdigheter
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedProfile.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        color: '#10b981'
                      }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              className="sticky bottom-0 p-6 border-t"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
              }}
            >
              <button
                onClick={() => setShowProfileModal(false)}
                className="w-full py-3 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: '#10b981',
                  color: 'white',
                }}
              >
                Lukk profil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

